import { useState } from "react";
import { Voucher } from "@/lib/accounting/types";
import { useAccounting } from "@/contexts/AccountingContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Smartphone, CheckCircle2, IndianRupee, Copy, QrCode } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── UPI QR Code Generator ───────────────────────────────────────────────

const DEFAULT_UPI_ID = "abctrading@okicici";
const DEFAULT_PAYEE_NAME = "ABC Trading Co.";

export function generateUPILink(amount: number, invoiceNumber: string, upiId = DEFAULT_UPI_ID, payeeName = DEFAULT_PAYEE_NAME): string {
  const params = new URLSearchParams({
    pa: upiId,
    pn: payeeName,
    am: amount.toFixed(2),
    cu: "INR",
    tn: `Payment for Invoice ${invoiceNumber}`,
    tr: invoiceNumber.replace(/[^a-zA-Z0-9]/g, ""),
  });
  return `upi://pay?${params.toString()}`;
}

export function getQRCodeURL(data: string, size = 200): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}&margin=8`;
}

// ── Invoice UPI Section (for templates) ─────────────────────────────────

export function renderUPISection(amount: number, invoiceNumber: string): string {
  const upiLink = generateUPILink(amount, invoiceNumber);
  const qrUrl = getQRCodeURL(upiLink, 150);

  return `
    <div style="margin-top:16px;padding:16px 24px;border-top:2px dashed #e2e8f0;background:#f0fdf4">
      <div style="display:flex;align-items:center;gap:16px">
        <div style="flex:1">
          <p style="font-size:13px;font-weight:600;color:#166534;margin-bottom:8px">💳 Pay via UPI</p>
          <p style="font-size:12px;color:#4b5563;margin-bottom:4px"><strong>UPI ID:</strong> ${DEFAULT_UPI_ID}</p>
          <p style="font-size:12px;color:#4b5563;margin-bottom:4px"><strong>Amount:</strong> ₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
          <p style="font-size:12px;color:#4b5563;margin-bottom:8px"><strong>Ref:</strong> ${invoiceNumber}</p>
          <p style="font-size:10px;color:#9ca3af">Scan QR code with any UPI app: Google Pay, PhonePe, Paytm, BHIM</p>
        </div>
        <div style="text-align:center">
          <img src="${qrUrl}" alt="UPI QR Code" style="width:150px;height:150px;border:2px solid #e5e7eb;border-radius:8px" />
          <p style="font-size:9px;color:#9ca3af;margin-top:4px">Scan to Pay</p>
        </div>
      </div>
    </div>`;
}

// ── UPI QR Preview Component ─────────────────────────────────────────────

interface UPIQRPreviewProps {
  voucher: Voucher;
}

export function UPIQRPreview({ voucher }: UPIQRPreviewProps) {
  const amount = voucher.totalDebit;
  const invoiceNum = voucher.invoiceNumber || voucher.voucherNumber;
  const upiLink = generateUPILink(amount, invoiceNum);
  const qrUrl = getQRCodeURL(upiLink, 180);
  const { toast } = useToast();

  const copyUPIId = () => {
    navigator.clipboard.writeText(DEFAULT_UPI_ID);
    toast({ title: "UPI ID copied" });
  };

  return (
    <div className="p-4 rounded-lg bg-success/5 border border-success/20 space-y-3">
      <div className="flex items-center gap-2">
        <Smartphone className="h-4 w-4 text-success" />
        <span className="text-sm font-semibold text-foreground">Pay via UPI</span>
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">UPI ID:</span>
            <code className="text-xs font-mono text-foreground bg-muted px-2 py-0.5 rounded">{DEFAULT_UPI_ID}</code>
            <button onClick={copyUPIId} className="text-muted-foreground hover:text-foreground"><Copy className="h-3 w-3" /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Amount:</span>
            <span className="text-sm font-bold text-success">₹{amount.toLocaleString("en-IN")}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Invoice:</span>
            <span className="text-xs font-medium text-foreground">{invoiceNum}</span>
          </div>
          <p className="text-[10px] text-muted-foreground">Scan with Google Pay, PhonePe, Paytm, or BHIM</p>
        </div>
        <div className="text-center">
          <img src={qrUrl} alt="UPI QR" className="w-[120px] h-[120px] rounded-lg border border-border" />
          <p className="text-[10px] text-muted-foreground mt-1">Scan to Pay</p>
        </div>
      </div>
    </div>
  );
}

// ── Record UPI Payment Dialog ────────────────────────────────────────────

interface RecordUPIPaymentDialogProps {
  voucher: Voucher;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type UPIProviderOption = "Google Pay" | "PhonePe" | "Paytm" | "BHIM" | "Other";

export function RecordUPIPaymentDialog({ voucher, open, onOpenChange }: RecordUPIPaymentDialogProps) {
  const { createVoucher, ledgers } = useAccounting();
  const { toast } = useToast();

  const [utrNumber, setUtrNumber] = useState("");
  const [provider, setProvider] = useState<UPIProviderOption>("Google Pay");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(voucher.totalDebit.toString());

  const handleRecordPayment = async () => {
    const trimmedUTR = utrNumber.trim();
    if (!trimmedUTR) {
      toast({ title: "UTR Required", description: "Please enter the UPI transaction reference number.", variant: "destructive" });
      return;
    }
    if (trimmedUTR.length < 6 || trimmedUTR.length > 30) {
      toast({ title: "Invalid UTR", description: "UTR number should be 6-30 characters.", variant: "destructive" });
      return;
    }

    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      toast({ title: "Invalid Amount", description: "Please enter a valid payment amount.", variant: "destructive" });
      return;
    }

    // Create a Receipt voucher: Debit Bank, Credit Customer
    const bankLedger = ledgers.find((l) => l.group === "Bank Accounts");
    const partyLedgerId = voucher.partyLedgerId;
    const partyName = voucher.partyName;

    if (!bankLedger || !partyLedgerId) {
      toast({ title: "Error", description: "Bank account or party ledger not found.", variant: "destructive" });
      return;
    }

    const partyLedger = ledgers.find((l) => l.id === partyLedgerId);
    if (!partyLedger) {
      toast({ title: "Error", description: "Party ledger not found.", variant: "destructive" });
      return;
    }

    const lines = [
      { id: crypto.randomUUID(), ledgerId: bankLedger.id, ledgerName: bankLedger.name, debit: paymentAmount, credit: 0 },
      { id: crypto.randomUUID(), ledgerId: partyLedgerId, ledgerName: partyLedger.name, debit: 0, credit: paymentAmount },
    ];

    const narration = `UPI Payment received via ${provider} | UTR: ${trimmedUTR} | Invoice: ${voucher.invoiceNumber || voucher.voucherNumber}`;

    const result = await createVoucher("Receipt", lines, narration, partyLedgerId);

    if (result.success) {
      toast({
        title: "Payment Recorded ✓",
        description: `₹${paymentAmount.toLocaleString("en-IN")} received via ${provider}. Receipt ${result.voucher?.voucherNumber} created.`,
      });
      onOpenChange(false);
      setUtrNumber("");
    } else {
      toast({ title: "Error", description: result.error, variant: "destructive" });
    }
  };

  const qrUrl = getQRCodeURL(generateUPILink(voucher.totalDebit, voucher.invoiceNumber || voucher.voucherNumber), 140);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <IndianRupee className="h-5 w-5 text-success" />
            Record UPI Payment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invoice Info */}
          <div className="p-3 rounded-lg bg-muted/50 border border-border flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">{voucher.invoiceNumber || voucher.voucherNumber}</p>
              <p className="text-xs text-muted-foreground">{voucher.partyName || "Unknown party"}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-foreground">₹{voucher.totalDebit.toLocaleString("en-IN")}</p>
              <Badge variant="outline" className="text-[10px]">{voucher.type}</Badge>
            </div>
          </div>

          {/* QR Code */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-success/5 border border-success/20">
            <img src={qrUrl} alt="UPI QR" className="w-[100px] h-[100px] rounded-lg border border-border" />
            <div className="space-y-1">
              <p className="text-xs font-semibold text-success">Share this QR with customer</p>
              <p className="text-xs text-muted-foreground">UPI ID: <code className="font-mono">{DEFAULT_UPI_ID}</code></p>
              <p className="text-[10px] text-muted-foreground">Works with Google Pay, PhonePe, Paytm, BHIM</p>
            </div>
          </div>

          {/* Payment Details Form */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">UPI Provider</label>
              <Select value={provider} onValueChange={(v) => setProvider(v as UPIProviderOption)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(["Google Pay", "PhonePe", "Paytm", "BHIM", "Other"] as UPIProviderOption[]).map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Payment Date</label>
              <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="h-9 text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">UTR / Transaction Reference *</label>
            <Input
              value={utrNumber}
              onChange={(e) => setUtrNumber(e.target.value)}
              placeholder="Enter UTR number from payment confirmation"
              className="h-9 text-sm font-mono"
              maxLength={30}
            />
            <p className="text-[10px] text-muted-foreground mt-1">Found in UPI payment confirmation SMS or app</p>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">Amount Received (₹)</label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-9 text-sm"
              min="0"
              step="0.01"
            />
            {parseFloat(amount) !== voucher.totalDebit && parseFloat(amount) > 0 && (
              <p className="text-[10px] text-warning mt-1">
                ⚠ Partial payment: ₹{(voucher.totalDebit - parseFloat(amount)).toLocaleString("en-IN")} balance remaining
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleRecordPayment} className="gap-1.5 bg-success text-white hover:bg-success/90">
            <CheckCircle2 className="h-4 w-4" /> Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
